import { type MarketingLocale, useLocalized } from '~/lib/marketing-locale'
import { ScrollReveal } from './scroll-reveal'
import { TruchetField } from './truchet-field'

/*
 * Finance — receivables table on top, a small KPI strip, then a side-by-side
 * (P&L table | tax + payment-schedule chain). No three-card row.
 *
 * Copy is locale-keyed — see the note in `crm-section.tsx`. The Brazilian
 * tax panel breaks out ISS/IRPJ/PIS-COFINS; the English one shows the
 * generic burden a US practice actually tracks, since naming Brazilian
 * federal taxes on a US page would be noise.
 */

type Receivable = {
  client: string
  desc: string
  installment: string
  status: string
  tone: string
  value: string
}
type Kpi = { label: string; tone: string; value: string }
type PlRow = { kind: 'revenue' | 'expense' | 'result'; label: string; value: string }
type TaxRow = { label: string; value: string }

type FinanceContent = {
  eyebrow: string
  heading: string
  intro: string
  mockupTitle: string
  filterLabel: string
  newEntryLabel: string
  kpis: readonly Kpi[]
  receivablesLabel: string
  columns: readonly [string, string, string, string]
  receivables: readonly Receivable[]
  pl: { eyebrow: string; headline: string; rows: readonly PlRow[] }
  fiscal: {
    eyebrow: string
    headline: string
    scheduleLabel: string
    scheduleNote: string
    taxes: readonly TaxRow[]
  }
}

const CONTENT: Record<MarketingLocale, FinanceContent> = {
  en: {
    columns: ['Description', 'Payment', 'Amount', 'Status'],
    eyebrow: 'FINANCE',
    filterLabel: 'Filter',
    fiscal: {
      eyebrow: 'TAX & PAYMENT SCHEDULES',
      headline: 'They take care of themselves',
      scheduleLabel: 'PAYMENTS · WHITFIELD',
      scheduleNote: '3 received · next due May 20',
      taxes: [
        { label: 'Payroll burden', value: '9.2%' },
        { label: 'State & local', value: '2.1%' },
        { label: 'Other', value: '2.15%' },
        { label: 'Total load', value: '13.45%' },
      ],
    },
    heading: 'The practice’s cash, finally legible',
    intro:
      'Payment schedules that unfold on their own, and a P&L that shows where the margin came from — and where it went.',
    kpis: [
      { label: 'Cash balance', tone: '#3e7d33', value: '$184,320' },
      { label: 'Receivable this month', tone: '#2a5dc1', value: '$62,500' },
      { label: 'Payable this month', tone: '#a46a18', value: '$28,140' },
      { label: 'Margin this month', tone: '#7a6cb6', value: '38%' },
    ],
    mockupTitle: 'Finance · May',
    newEntryLabel: '+ New entry',
    pl: {
      eyebrow: 'SIMPLE P&L · MAY',
      headline: '+ $101,040 result',
      rows: [
        { kind: 'revenue', label: 'Design fees', value: '$142,300' },
        { kind: 'revenue', label: 'One-off consulting', value: '$12,400' },
        { kind: 'expense', label: 'Team', value: '−$38,200' },
        { kind: 'expense', label: 'Taxes', value: '−$11,840' },
        { kind: 'expense', label: 'Software and licenses', value: '−$3,620' },
        { kind: 'result', label: 'Result for the month', value: '+$101,040' },
      ],
    },
    receivables: [
      {
        client: 'Aurora Developments',
        desc: 'Aurora Tower, deposit',
        installment: '1/8',
        status: 'Received',
        tone: '#7bb86b',
        value: '$27,500',
      },
      {
        client: 'The Harpers',
        desc: 'Whitfield House, payment 4/6',
        installment: '4/6',
        status: 'Due',
        tone: '#5b8def',
        value: '$7,200',
      },
      {
        client: 'Carmine Studio',
        desc: 'Foundry Lane, final',
        installment: '6/6',
        status: 'Overdue',
        tone: '#e0593f',
        value: '$5,400',
      },
      {
        client: 'Marcus Ellery',
        desc: 'Marlow Loft, payment 2/4',
        installment: '2/4',
        status: 'Due',
        tone: '#5b8def',
        value: '$6,000',
      },
    ],
    receivablesLabel: 'Receivable',
  },
  'pt-BR': {
    columns: ['Descrição', 'Parcela', 'Valor', 'Status'],
    eyebrow: 'FINANCEIRO',
    filterLabel: 'Filtrar',
    fiscal: {
      eyebrow: 'FISCAL & PARCELAS',
      headline: 'Cuidam de si mesmas',
      scheduleLabel: 'PARCELAS · LOPES',
      scheduleNote: '3 recebidas · próxima vence 20/05',
      taxes: [
        { label: 'ISS', value: '5%' },
        { label: 'IRPJ', value: '4,8%' },
        { label: 'PIS / COFINS', value: '3,65%' },
        { label: 'Carga total', value: '13,45%' },
      ],
    },
    heading: 'O caixa do escritório, finalmente legível',
    intro:
      'Parcelas que se desdobram sozinhas, DRE que mostra de onde a margem vem, e para onde foi.',
    kpis: [
      { label: 'Saldo em caixa', tone: '#3e7d33', value: 'R$ 184.320' },
      { label: 'A receber este mês', tone: '#2a5dc1', value: 'R$ 62.500' },
      { label: 'A pagar este mês', tone: '#a46a18', value: 'R$ 28.140' },
      { label: 'Margem mês', tone: '#7a6cb6', value: '38%' },
    ],
    mockupTitle: 'Financeiro · maio',
    newEntryLabel: '+ Novo lançamento',
    pl: {
      eyebrow: 'DRE SIMPLIFICADO · MAIO',
      headline: '+ R$ 101.040 de resultado',
      rows: [
        { kind: 'revenue', label: 'Honorários de projeto', value: 'R$ 142.300' },
        { kind: 'revenue', label: 'Consultoria avulsa', value: 'R$ 12.400' },
        { kind: 'expense', label: 'Equipe', value: '−R$ 38.200' },
        { kind: 'expense', label: 'Impostos (ISS + DAS)', value: '−R$ 11.840' },
        { kind: 'expense', label: 'Softwares e licenças', value: '−R$ 3.620' },
        { kind: 'result', label: 'Resultado do mês', value: '+R$ 101.040' },
      ],
    },
    receivables: [
      {
        client: 'Aurora Inc.',
        desc: 'Edifício Aurora, Entrada',
        installment: '1/8',
        status: 'Recebido',
        tone: '#7bb86b',
        value: 'R$ 27.500',
      },
      {
        client: 'Família Lopes',
        desc: 'Residência Lopes, Parcela 4/6',
        installment: '4/6',
        status: 'A receber',
        tone: '#5b8def',
        value: 'R$ 7.200',
      },
      {
        client: 'Studio Carmim',
        desc: 'Loja Centro, Final',
        installment: '6/6',
        status: 'Atrasado',
        tone: '#e0593f',
        value: 'R$ 5.400',
      },
      {
        client: 'Marcos Almeida',
        desc: 'Reforma Almeida, Parcela 2/4',
        installment: '2/4',
        status: 'A receber',
        tone: '#5b8def',
        value: 'R$ 6.000',
      },
    ],
    receivablesLabel: 'A receber',
  },
}

const FinanceMockup = ({ content }: { content: FinanceContent }) => (
  <div className="overflow-hidden rounded-2xl border border-[#ececec] bg-white shadow-[0_24px_60px_-26px_rgba(20,80,40,0.28)]">
    <div className="flex items-center justify-between border-[#f1f1f1] border-b px-5 py-3">
      <span className="font-semibold text-[#1c1c20] text-[13px]">{content.mockupTitle}</span>
      <div className="flex items-center gap-2">
        <span className="rounded-md border border-[#ececec] px-2 py-1 text-[#6c6c72] text-[10.5px]">
          {content.filterLabel}
        </span>
        <span className="rounded-md bg-[#1d7a4c] px-2 py-1 text-[10.5px] text-white">
          {content.newEntryLabel}
        </span>
      </div>
    </div>

    <div className="grid grid-cols-4 gap-2 px-5 py-4">
      {content.kpis.map((kpi) => (
        <div className="rounded-xl border border-[#efefef] bg-white p-3" key={kpi.label}>
          <p className="text-[#9a9aa0] text-[10px]">{kpi.label}</p>
          <p className="mt-1 font-semibold text-[#1c1c20] text-[16px]">{kpi.value}</p>
          <span
            className="mt-1.5 inline-block size-[6px] rounded-full"
            style={{ background: kpi.tone }}
          />
        </div>
      ))}
    </div>

    <div className="px-5 pb-4">
      <p className="pb-2 font-semibold text-[#1c1c20] text-[11.5px]">{content.receivablesLabel}</p>
      <div className="overflow-hidden rounded-xl border border-[#efefef]">
        <div className="grid grid-cols-[1.8fr_0.6fr_0.7fr_0.7fr] bg-[#fafafa] px-3 py-2 text-[#9a9aa0] text-[10px]">
          {content.columns.map((column) => (
            <span key={column}>{column}</span>
          ))}
        </div>
        {content.receivables.map((row) => (
          <div
            className="grid grid-cols-[1.8fr_0.6fr_0.7fr_0.7fr] items-center border-[#f4f4f4] border-t px-3 py-2.5 text-[11.5px]"
            key={row.desc}
          >
            <div>
              <p className="font-medium text-[#1c1c20] leading-tight">{row.desc}</p>
              <p className="text-[#9a9aa0] text-[10px]">{row.client}</p>
            </div>
            <span className="w-fit rounded bg-[#f1f1f1] px-1.5 py-0.5 text-[#6c6c72] text-[10.5px]">
              ▶ {row.installment}
            </span>
            <span className="font-semibold text-[#43434a]">{row.value}</span>
            <span
              className="w-fit rounded px-2 py-0.5 font-medium text-[10.5px]"
              style={{ background: `${row.tone}22`, color: row.tone }}
            >
              {row.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
)

const PlPanel = ({ content }: { content: FinanceContent }) => (
  <div className="overflow-hidden rounded-2xl border border-[#dfeedb] bg-white">
    <div className="border-[#f1f1f1] border-b bg-gradient-to-br from-[#eef6ec] to-white px-5 py-3">
      <p className="text-[#3e7d33] text-[10.5px] tracking-[0.18em]">{content.pl.eyebrow}</p>
      <p className="mt-1 font-bold text-[#0b0b0d] text-[18px]">{content.pl.headline}</p>
    </div>
    <div className="px-5 py-4">
      {content.pl.rows.map((row) => (
        <div
          className="flex items-center justify-between border-[#f4f4f4] border-t py-2 first:border-t-0 first:pt-0"
          key={row.label}
        >
          <span
            className={
              row.kind === 'result'
                ? 'font-semibold text-[#0b0b0d] text-[12.5px]'
                : 'text-[#43434a] text-[12.5px]'
            }
          >
            {row.label}
          </span>
          <span
            className={
              row.kind === 'result'
                ? 'font-bold text-[#3e7d33] text-[14px]'
                : row.kind === 'expense'
                  ? 'font-medium text-[#c4502f] text-[12.5px]'
                  : 'font-medium text-[#3e7d33] text-[12.5px]'
            }
          >
            {row.value}
          </span>
        </div>
      ))}
    </div>
  </div>
)

const FiscalPanel = ({ content }: { content: FinanceContent }) => (
  <div className="overflow-hidden rounded-2xl border border-[#dfeedb] bg-[#0b0b0d] text-white">
    <div className="border-white/10 border-b px-5 py-3">
      <p className="text-[#7bb86b] text-[10.5px] tracking-[0.18em]">{content.fiscal.eyebrow}</p>
      <p className="mt-1 font-bold text-[18px]">{content.fiscal.headline}</p>
    </div>
    <div className="space-y-3 px-5 py-4">
      {/* Payment-schedule chain */}
      <div className="rounded-xl bg-white/[0.04] p-3">
        <p className="text-[10px] text-white/60 tracking-[0.16em]">
          {content.fiscal.scheduleLabel}
        </p>
        <div className="mt-2 flex flex-wrap gap-1">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
            <span
              className="rounded-md px-2 py-1 font-medium text-[10.5px]"
              key={n}
              style={{
                background: n <= 3 ? '#dfeedb' : 'rgba(255,255,255,0.08)',
                color: n <= 3 ? '#3e7d33' : '#a8a3b4',
              }}
            >
              {n}/8
            </span>
          ))}
        </div>
        <p className="mt-2 text-[10px] text-white/60">{content.fiscal.scheduleNote}</p>
      </div>
      {/* Tax breakdown */}
      <div className="grid grid-cols-2 gap-2">
        {content.fiscal.taxes.map((row) => (
          <div className="rounded-md bg-white/[0.04] px-2.5 py-2" key={row.label}>
            <p className="text-[10px] text-white/60">{row.label}</p>
            <p className="font-bold text-[14px]">{row.value}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
)

export const FinanceSection = () => {
  const content = useLocalized(CONTENT)

  return (
    <section
      className="relative overflow-hidden py-28"
      style={{ background: 'linear-gradient(180deg, #ffffff 0%, #eef6ec 40%, #e3eee0 100%)' }}
    >
      <TruchetField
        className="absolute inset-0 h-full w-full text-[#0b0b0d] opacity-[0.05]"
        seed="finance"
      />
      <div className="relative mx-auto max-w-[1120px] px-6">
        <ScrollReveal className="text-center">
          <p className="text-[#3e7d33] text-[12px] tracking-[0.2em]">{content.eyebrow}</p>
          <h2 className="mx-auto mt-3 max-w-[820px] font-display text-[#0b0b0d] text-[clamp(40px,6vw,58px)] leading-[1.06] tracking-[-0.01em]">
            {content.heading}
          </h2>
          <p className="mx-auto mt-5 max-w-[640px] text-[#6c6c72] text-[clamp(16px,2.2vw,20px)]">
            {content.intro}
          </p>
        </ScrollReveal>

        <ScrollReveal className="mt-14" delay={100}>
          <FinanceMockup content={content} />
        </ScrollReveal>

        {/* Asymmetric pair, P&L on light, tax on dark */}
        <div className="mt-8 grid items-stretch gap-6 md:grid-cols-2">
          <ScrollReveal>
            <PlPanel content={content} />
          </ScrollReveal>
          <ScrollReveal delay={120}>
            <FiscalPanel content={content} />
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
